/**
 * Avatar — a circular portrait built on Radix Avatar. Shows the image when it
 * loads, otherwise a fallback initial derived from `name` (or an explicit
 * `fallback`). Three sizes.
 */
import { forwardRef, type ComponentPropsWithoutRef, type ReactNode } from "react";
import * as RadixAvatar from "@radix-ui/react-avatar";

export type AvatarSize = "sm" | "md" | "lg";

export interface AvatarProps
  extends Omit<ComponentPropsWithoutRef<typeof RadixAvatar.Root>, "children"> {
  /** Image source. Falls back to the initial if missing or fails to load. */
  readonly src?: string;
  /** Name used both for alt text and the fallback initial. */
  readonly name?: string;
  /** Explicit fallback node (overrides the derived initial). */
  readonly fallback?: ReactNode;
  /** Size. @default "md" */
  readonly size?: AvatarSize;
}

function initialOf(name?: string): string {
  if (!name) return "?";
  const trimmed = name.trim();
  return trimmed.length > 0 ? Array.from(trimmed)[0]!.toUpperCase() : "?";
}

export const Avatar = forwardRef<HTMLSpanElement, AvatarProps>(function Avatar(
  { src, name, fallback, size = "md", className, ...rest },
  ref,
) {
  const classes = ["lc-avatar", `lc-avatar--${size}`, className]
    .filter(Boolean)
    .join(" ");
  return (
    <RadixAvatar.Root ref={ref} className={classes} {...rest}>
      {src ? (
        <RadixAvatar.Image className="lc-avatar__image" src={src} alt={name ?? ""} />
      ) : null}
      <RadixAvatar.Fallback className="lc-avatar__fallback" delayMs={src ? 300 : 0}>
        {fallback ?? initialOf(name)}
      </RadixAvatar.Fallback>
    </RadixAvatar.Root>
  );
});
